<template>
  <!-- Карточка «Обращения филиала» (p6): полоска статуса, три строки, чипы справа -->
  <article
    class="appeal-card"
    :class="{ 'appeal-card--unread': appeal.unreadCount > 0 }"
    role="button"
    tabindex="0"
    @click="$emit('open', appeal)"
    @keydown.enter="$emit('open', appeal)"
  >
    <span class="appeal-card__strip" :class="`appeal-card__strip--${tone}`" />
    <support-avatar
      :src="appeal.createdBy && appeal.createdBy.avatar"
      :name="appeal.createdBy && appeal.createdBy.name"
      :size="40"
    />
    <div class="appeal-card__body">
      <div class="appeal-card__line1">
        <span class="appeal-card__subject">{{ appeal.subject || 'Без темы' }}</span>
        <span class="appeal-card__number">- {{ appeal.number }}</span>
        <span class="appeal-card__dot">·</span>
        <appeal-status-text :appeal="appeal" />
        <!-- Новые ответы поддержки: красный счётчик сразу за статусом -->
        <span v-if="appeal.unreadCount > 0" class="appeal-card__unread">{{ appeal.unreadCount }}</span>
      </div>
      <!-- Без «создала/создал»: пола автора в данных нет -->
      <div class="appeal-card__line2">
        {{ authorLine }}
      </div>
      <div class="appeal-card__line3" :class="{ 'appeal-card__line3--support': fromSupport }">
        {{ preview }}
      </div>
    </div>
    <div class="appeal-card__chips">
      <category-chip :category="appeal.category" :accent="appeal.status === 'new'" />
      <assignee-chip :appeal="appeal" />
    </div>
  </article>
</template>

<script>
  import AppealStatusText from './AppealStatusText.vue'
  import AssigneeChip from './AssigneeChip.vue'
  import CategoryChip from './CategoryChip.vue'
  import SupportAvatar from './SupportAvatar.vue'
  import { rowTime } from './format'
  import { statusView } from './status'

  export default {
    name: 'AppealCard',
    components: { AppealStatusText, AssigneeChip, CategoryChip, SupportAvatar },
    props: {
      appeal: { type: Object, required: true },
    },
    computed: {
      tone () {
        return statusView(this.appeal).tone
      },
      authorLine () {
        const name = this.appeal.createdBy?.name

        return [name, rowTime(this.appeal.createdAt)].filter(Boolean).join(' · ')
      },
      fromSupport () {
        return this.appeal.lastMessage?.from === 'support'
      },
      // «Вы: …» за любого сотрудника филиала, «Поддержка: …» за поддержку; только фото - «Фото».
      preview () {
        const last = this.appeal.lastMessage
        if (!last) return ''

        return `${this.fromSupport ? 'Поддержка' : 'Вы'}: ${last.text || 'Фото'}`
      },
    },
  }
</script>

<style scoped lang="less">
@import './tokens.less';

.appeal-card {
  position: relative;
  display: flex;
  gap: 12px;
  align-items: center;
  min-height: 125px;
  padding: 16px;
  border-radius: 8px;
  background: @fakt-white;
  cursor: pointer;
  transition: background-color 0.16s ease;

  &:hover {
    background: @fakt-canvas;
  }

  // Новый ответ поддержки - бежевый фон карточки
  &--unread,
  &--unread:hover {
    background: @sup-new-bg;
  }
}

.appeal-card__strip {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  width: 4px;
  border-radius: 0 2px 2px 0;

  &--new { background: @sup-new; }
  &--work { background: @sup-work; }
  &--wait { background: @sup-wait; }
  &--done { background: @sup-done; }
  &--muted,
  &--idea { background: @fakt-text-3; }
}

.appeal-card__body {
  flex: 1;
  min-width: 0;
}

.appeal-card__line1 {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  font-size: 14px;
}

.appeal-card__subject {
  font-weight: 500;
  color: @fakt-text;
}

.appeal-card__number,
.appeal-card__dot {
  color: @fakt-text-3;
}

.appeal-card__unread {
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 500;
  line-height: 20px;
  text-align: center;
  color: @fakt-white;
  background: @fakt-error;
}

.appeal-card__line2 {
  margin-top: 4px;
  font-size: 12px;
  color: @fakt-text-2;
}

.appeal-card__line3 {
  margin-top: 8px;
  overflow: hidden;
  font-size: 14px;
  font-weight: 300;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: @fakt-text;

  &--support {
    color: @fakt-text-2;
  }
}

.appeal-card__chips {
  display: flex;
  flex-shrink: 0;
  gap: 8px;
  align-items: center;
}

// Узкий экран: чипы уходят под текст
.media-md({
  .appeal-card {
    flex-wrap: wrap;
  }

  .appeal-card__chips {
    width: 100%;
    padding-left: 52px;
  }
});
</style>
